<template>
  <div>
    <img
      v-if="isSearchByVisa"
      src="/visa-result-banner.png"
      alt="back"
      class="absolute left-0 top-0 -z-1 w-full py-[20vh] print:hidden"
    />

    <TheMobileHeader
      v-if="!isDesktop"
      :title="$t('search.searchResult')"
      showBack
      :showHelpIcon="false"
      showItinerary
      :showSearchHistory="!isSearchByVisa"
      class="bg-white print:hidden"
      isFixed
      @back="isOpenBackConfirm = true"
    />

    <JContainer
      class="[&>div]:flex [&>div]:flex-col [&>div]:gap-y-20"
      :class="
        searchStore.isSearched && !isSearchByVisa ? 'pt-[16rem]' : 'pt-[8rem]'
      "
    >
      <template v-if="!isSearchByVisa">
        <MultiCitySearchResultSelector
          v-if="isMultiCitySearchByAttributes"
          v-model="city"
          class="mb-24 rounded-t-lg border-b border-line bg-gray-100 px-24 pb-24 md:py-24"
          includeHotelOptions
          @update:model-value="onSelectCity"
        />
        <MobileAirCraftsResultsCarousel
          v-if="shouldRender.aircraft"
          :key="searchId"
          :style="{ order: sectionsOrders.aircraft }"
        />
        <MobileFlightResultsCarousel
          :key="searchId"
          ref="flightResults"
          :style="{ order: sectionsOrders.flight }"
        />
        <MobileHotelResultsCarousel
          v-if="shouldRender.hotel"
          :key="searchId"
          ref="hotelResults"
          :style="{ order: sectionsOrders.hotel }"
        />
        <MobileCarResultsCarousel
          v-if="shouldRender.car"
          :key="searchId"
          ref="carResults"
          :style="{ order: sectionsOrders.car }"
        />
      </template>

      <VisaResult v-else />
    </JContainer>

    <RefreshResultsDialog
      v-model="refreshTimerStore.isRefreshDialogOpen"
      @refresh="onRefresh"
    />

    <JModal
      :modelValue="serverErrorModalIsOpen"
      size="sm"
      center
      @update:model-value="setServerErrorModalIsOpen"
    >
      <ServerError @retry="onRefresh" />
    </JModal>

    <JConfirmation
      v-model="isOpenBackConfirm"
      :cancelText="$t('words.cancel')"
      :confirmText="$t('words.goBack')"
      :title="$t('search.backConfirm.title')"
      :description="$t('search.backConfirm.description')"
      @confirm="onBackToSearch"
      @cancel="isOpenBackConfirm = false"
    />
  </div>

  <template v-if="shouldRender.aircraft">
    <AircraftBookBottomSheet v-model="isOpenBookingAircraft" />
    <AircraftConfirmModal v-model="isOpenSuccessModal" />
  </template>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { useToggle } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { JConfirmation, JContainer, JModal } from '@journey/ui-kit'
import { storeToRefs } from 'pinia'
import useSearchStore from '../store'
import MobileFlightResultsCarousel from '@/modules/Search/components/FlightResults/MobileFlightResultsCarousel/MobileFlightResultsCarousel.vue'
import MobileHotelResultsCarousel from '@/modules/Search/components/HotelResults/MobileHotelResultsCarousel/MobileHotelResultsCarousel.vue'
import MobileCarResultsCarousel from '@/modules/Search/components/CarResults/MobileCarResultsCarousel/MobileCarResultsCarousel.vue'

// import SpecialOffers from '@/modules/Search/components/SpecialOffers/SpecialOffers.vue'
import RefreshResultsDialog from '@/modules/Search/components/RefreshResultsDialog/RefreshResultsDialog.vue'
import { refreshTimer } from '@/modules/Search/store/refreshTimer'
import { DEFAULT_REFRESH_TIMER } from '@/modules/Search/constants'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import { useSearch, useSearchRenderResults } from '@/modules/Search/composables'
import ServerError from '@/modules/Core/components/shared/ServerError/ServerError.vue'
import useHotelSearchResultsStore from '@/modules/Search/store/hotelSearchResults'
import TheMobileHeader from '@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue'
import { useDevicePlatform } from '@/modules/Core/composables'
import MultiCitySearchResultSelector from '@/modules/Search/components/MultiCitySearchResultSelector/MultiCitySearchResultSelector.vue'
import useFlightSearchResultsStore from '@/modules/Search/store/flightSearchResults'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import MobileAirCraftsResultsCarousel from '@/modules/Search/components/AirCraftsResults/MobileAirCraftsResultsCarousel/MobileAirCraftsResultsCarousel.vue'
import useBookingAircraftStore from '@/modules/Book/store/bookingAirCraft'
import featureFlag from '@/config/featureFlag'

const AircraftBookBottomSheet = defineAsyncComponent(
  () =>
    import(
      '@/modules/Book/components/Aircraft/AircraftBookBottomSheet/AircraftBookBottomSheet.vue'
    )
)
const AircraftConfirmModal = defineAsyncComponent(
  () =>
    import(
      '@/modules/Book/components/Aircraft/AircraftConfirmModal/AircraftConfirmModal.vue'
    )
)

const VisaResult = defineAsyncComponent(
  () => import('@/modules/Search/components/Visa/VisaResult/VisaResult.vue')
)

const router = useRouter()

const { t } = useI18n()
const { isDesktop } = useDevicePlatform()

const refreshTimerStore = refreshTimer()
const hotelSearchResultStore = useHotelSearchResultsStore()
const flightSearchResultsStore = useFlightSearchResultsStore()
const searchPanelHistoryStore = useSearchPanelHistoryStore()
const itinerarySummaryStore = useItinerarySummary()
const searchStore = useSearchStore()
const { onSearch } = useSearch()

const [serverErrorModalIsOpen, setServerErrorModalIsOpen] = useToggle(false)

const flightResults = ref<InstanceType<typeof MobileFlightResultsCarousel>>()
const hotelResults = ref<InstanceType<typeof MobileHotelResultsCarousel>>()
const carResults = ref<InstanceType<typeof MobileCarResultsCarousel>>()

const sectionsOrders = computed(() => {
  const config = featureFlag.searchResultsPriority
  return {
    aircraft: config?.indexOf('aircraft') ?? 1,
    flight: config?.indexOf('flight') ?? 2,
    hotel: config?.indexOf('hotel') ?? 3,
    car: config?.indexOf('car') ?? 4,
  }
})

async function onRefresh() {
  setServerErrorModalIsOpen(false)

  clearSelectedHotels()

  try {
    clearItinerary()
    refreshTimerStore.clearTimer()

    await onSearch()

    if (searchPanelHistoryStore.history.isSearchByVisa) {
      return
    }

    refreshTimerStore.startTimer(DEFAULT_REFRESH_TIMER)
  } catch (error) {
    setServerErrorModalIsOpen(true)
  }
}

function clearItinerary() {
  itinerarySummaryStore.items = []
  itinerarySummaryStore.itineraries = undefined
}
function clearSelectedHotels() {
  hotelSearchResultStore.rooms = []
}

useHead({
  titleTemplate: (title) => `${title} - ${t('search.result.title')}`,
})

const isOpenBackConfirm = ref(false)

function onBackToSearch() {
  router.push({ name: 'Search' })
}

const isMultiCitySearchByAttributes = computed(
  () => searchPanelHistoryStore.history.isSearchByAttributes
)

const searchId = computed(
  () => searchPanelHistoryStore.history.searchId as string
)

const city = ref()

function onSelectCity(value?: string) {
  city.value = value
  flightSearchResultsStore.setFilteredCity(value)
  carResults.value?.onSelectCity(value)
  hotelResults.value?.onSelectCity(value)
  flightResults.value?.onSelectCity(value)
}
const { isSearchLoading } = useSearch()

watch(
  () => isSearchLoading.value,
  (isSearchLoading) => {
    if (isSearchLoading) {
      return
    }

    flightSearchResultsStore.filteredCity = undefined

    if (!isMultiCitySearchByAttributes.value) {
      return
    }

    city.value =
      searchPanelHistoryStore.history.attributeOptions[0]?.geoLocation
    flightSearchResultsStore.setFilteredCity(city.value)
  },
  {
    immediate: true,
  }
)

const { shouldRender } = useSearchRenderResults()

const bookingAirCraftStore = useBookingAircraftStore()
const { isOpenBookingAircraft, isOpenSuccessModal } =
  storeToRefs(bookingAirCraftStore)

const isSearchByVisa = computed(
  () => searchPanelHistoryStore.history.isSearchByVisa
)
</script>
